import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SettingsPageShell from "../../components/shared/SettingsPageShell";
import {
  cancelBtnSx,
  crudTableSx,
  flyoutInputSx,
  flyoutSelectSx,
  formErrorSx,
  neutralChipRemoveBtnSx,
  saveBtnSx,
  toolbarBtnSx,
} from "../../components/shared/crudStyles";
import {
  useTranscriptionConfig,
  useUpdateTranscriptionConfig,
} from "../../../hooks/useTranscriptionConfig";
import {
  REDACTION_CATEGORIES,
  RETENTION_PERIOD_UNITS,
  RuleFormValue,
  toFormValues,
  toPutRequest,
  TranscriptionConfigFormValues,
} from "../../../pages/customers/transcription-config/transcriptionConfigForm";
import type {
  RedactionCategory,
  RetentionPeriodUnit,
} from "../../../types/transcription/TranscriptionConfig";

const sectionTitleSx = {
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "var(--text-primary)",
  mb: 0.5,
};

const sectionHintSx = {
  fontSize: "0.8125rem",
  color: "var(--text-tertiary)",
  mb: 2,
};

const tagChipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  px: "8px",
  py: "3px",
  borderRadius: "12px",
  backgroundColor: "var(--accent-dim)",
  border: "1px solid var(--accent-glow)",
  color: "var(--accent)",
  fontSize: "0.75rem",
  fontWeight: 500,
};

const iconBtnSx = (disabled: boolean) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  borderRadius: "6px",
  border: "none",
  backgroundColor: "transparent",
  color: disabled ? "var(--border-mid)" : "var(--text-tertiary)",
  cursor: disabled ? "default" : "pointer",
  padding: 0,
  "&:hover": disabled
    ? {}
    : { backgroundColor: "var(--bg-hover)", color: "var(--text-primary)" },
});

export default function V2TranscriptionSettings() {
  const { t } = useTranslation(["transcriptionConfig", "common"]);
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useTranscriptionConfig(customerId);
  const updateMutation = useUpdateTranscriptionConfig(customerId);

  const [values, setValues] = useState<TranscriptionConfigFormValues>({
    categories: [],
    rules: [],
  });
  const [initialized, setInitialized] = useState(false);
  const [tagDrafts, setTagDrafts] = useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = useState<{
    text: string;
    type: "success" | "error";
  } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (initialized || !data) return;
    setValues(toFormValues(data));
    setInitialized(true);
  }, [data, initialized]);

  const toggleCategory = (index: number, enabled: boolean) => {
    setValues((prev) => ({
      ...prev,
      categories: prev.categories.map((c, i) =>
        i === index ? { ...c, enabled } : c,
      ),
    }));
    setSaveMessage(null);
  };

  const addTag = (index: number, category: RedactionCategory) => {
    const draft = (tagDrafts[category] ?? "").trim();
    if (!draft) return;
    setValues((prev) => ({
      ...prev,
      categories: prev.categories.map((c, i) =>
        i === index && !c.tags.includes(draft)
          ? { ...c, tags: [...c.tags, draft] }
          : c,
      ),
    }));
    setTagDrafts((prev) => ({ ...prev, [category]: "" }));
    setSaveMessage(null);
  };

  const removeTag = (index: number, tagIndex: number) => {
    setValues((prev) => ({
      ...prev,
      categories: prev.categories.map((c, i) =>
        i === index
          ? { ...c, tags: c.tags.filter((_, ti) => ti !== tagIndex) }
          : c,
      ),
    }));
    setSaveMessage(null);
  };

  const updateRule = (index: number, patch: Partial<RuleFormValue>) => {
    setValues((prev) => ({
      ...prev,
      rules: prev.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
    setSaveMessage(null);
  };

  const moveRule = (index: number, offset: number) => {
    setValues((prev) => {
      const rules = [...prev.rules];
      const [rule] = rules.splice(index, 1);
      rules.splice(index + offset, 0, rule);
      return { ...prev, rules };
    });
    setSaveMessage(null);
  };

  const removeRule = (index: number) => {
    setValues((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
    setSaveMessage(null);
  };

  const addRule = () => {
    setValues((prev) => ({
      ...prev,
      rules: [
        ...prev.rules,
        { category: "Financial", durationAfterStartDate: 1, unit: "Days" },
      ],
    }));
    setSaveMessage(null);
  };

  const handleSave = async () => {
    const hasInvalidDuration = values.rules.some(
      (r) =>
        !Number.isFinite(Number(r.durationAfterStartDate)) ||
        Number(r.durationAfterStartDate) < 1,
    );
    if (hasInvalidDuration) {
      setValidationError(
        `${t("transcriptionConfig:retainFor")}: ${t("transcriptionConfig:durationRequired")}`,
      );
      return;
    }
    setValidationError(null);

    try {
      await updateMutation.mutateAsync(toPutRequest(customerId!, values));
      setSaveMessage({
        text: t("transcriptionConfig:saveSuccess"),
        type: "success",
      });
    } catch {
      setSaveMessage({
        text: t("transcriptionConfig:saveError"),
        type: "error",
      });
    }
  };

  return (
    <SettingsPageShell
      isLoading={isLoading || !initialized}
      loadingMessage={t("common:loading")}
    >
      {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: "1px solid var(--border-subtle)",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              fontSize: "1.125rem",
              fontWeight: 600,
              color: "var(--text-primary)",
            }}
          >
            {t("transcriptionConfig:title")}
          </Box>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
          {/* Redaction categories */}
          <Box sx={sectionTitleSx}>
            {t("transcriptionConfig:redactionCategories")}
          </Box>
          <Box sx={sectionHintSx}>
            {t("transcriptionConfig:redactionCategoriesDescription")}
          </Box>
          <Box component="table" sx={{ ...crudTableSx, maxWidth: 800, mb: 4 }}>
            <thead>
              <tr>
                <Box component="th" sx={{ width: 80 }}>
                  {t("transcriptionConfig:enabled")}
                </Box>
                <Box component="th" sx={{ width: 140 }}>
                  {t("transcriptionConfig:category")}
                </Box>
                <th>{t("transcriptionConfig:tags")}</th>
              </tr>
            </thead>
            <tbody>
              {values.categories.map((row, index) => (
                <Box component="tr" key={row.category} sx={{ cursor: "default" }}>
                  <td>
                    <Box
                      component="input"
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        toggleCategory(index, e.target.checked)
                      }
                      sx={{
                        accentColor: "var(--accent)",
                        width: 14,
                        height: 14,
                        cursor: "pointer",
                      }}
                    />
                  </td>
                  <Box component="td" sx={{ color: "var(--text-primary)" }}>
                    {t(`transcriptionConfig:categories.${row.category}`)}
                  </Box>
                  <Box component="td" sx={{ whiteSpace: "normal" }}>
                    {row.enabled ? (
                      <>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {row.tags.map((tag, tagIndex) => (
                          <Box key={tag} sx={tagChipSx}>
                            {tag}
                            <Box
                              component="button"
                              type="button"
                              aria-label={`${t("common:delete")} ${tag}`}
                              onClick={() => removeTag(index, tagIndex)}
                              sx={neutralChipRemoveBtnSx}
                            >
                              ✕
                            </Box>
                          </Box>
                        ))}
                        <Box
                          component="input"
                          type="text"
                          placeholder={t("transcriptionConfig:addTag")}
                          value={tagDrafts[row.category] ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setTagDrafts((prev) => ({
                              ...prev,
                              [row.category]: e.target.value,
                            }))
                          }
                          onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addTag(index, row.category);
                            }
                          }}
                          onBlur={() => addTag(index, row.category)}
                          sx={{ ...flyoutInputSx, width: 160, py: "5px" }}
                        />
                      </Box>
                      <Box
                        sx={{
                          fontSize: "0.72rem",
                          color: "var(--text-tertiary)",
                          mt: "4px",
                        }}
                      >
                        {t("transcriptionConfig:addTagHelp")}
                      </Box>
                      </>
                    ) : (
                      <Box
                        component="span"
                        sx={{ color: "var(--text-tertiary)" }}
                      >
                        {t("transcriptionConfig:enableCategoryForTags")}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </tbody>
          </Box>

          {/* Retention rules */}
          <Box sx={sectionTitleSx}>
            {t("transcriptionConfig:retentionRules")}
          </Box>
          <Box sx={sectionHintSx}>
            {t("transcriptionConfig:retentionRulesDescription")}
          </Box>
          {values.rules.length === 0 ? (
            <Box sx={{ ...sectionHintSx, fontStyle: "italic" }}>
              {t("transcriptionConfig:noRules")}
            </Box>
          ) : (
            <Box component="table" sx={{ ...crudTableSx, maxWidth: 800 }}>
              <thead>
                <tr>
                  <Box component="th" sx={{ width: 110 }}>
                    {t("transcriptionConfig:priority")}
                  </Box>
                  <Box component="th" sx={{ width: 160 }}>
                    {t("transcriptionConfig:category")}
                  </Box>
                  <Box component="th" sx={{ width: 110 }}>
                    {t("transcriptionConfig:retainFor")}
                  </Box>
                  <Box component="th" sx={{ width: 130 }}>
                    {t("transcriptionConfig:unit")}
                  </Box>
                  <Box component="th" sx={{ width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {values.rules.map((rule, index) => (
                  <Box
                    component="tr"
                    key={`${rule.category}-${index}`}
                    sx={{ cursor: "default" }}
                  >
                    <td>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: "var(--text-primary)",
                            fontWeight: 600,
                            width: 16,
                            textAlign: "center",
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          aria-label={t("transcriptionConfig:moveUp")}
                          disabled={index === 0}
                          onClick={() => moveRule(index, -1)}
                          sx={iconBtnSx(index === 0)}
                        >
                          ↑
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          aria-label={t("transcriptionConfig:moveDown")}
                          disabled={index === values.rules.length - 1}
                          onClick={() => moveRule(index, 1)}
                          sx={iconBtnSx(index === values.rules.length - 1)}
                        >
                          ↓
                        </Box>
                      </Box>
                    </td>
                    <td>
                      <Box
                        component="select"
                        value={rule.category}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          updateRule(index, {
                            category: e.target.value as RedactionCategory,
                          })
                        }
                        sx={{ ...flyoutSelectSx, py: "6px" }}
                      >
                        {REDACTION_CATEGORIES.map((category) => (
                          <option key={category} value={category}>
                            {t(`transcriptionConfig:categories.${category}`)}
                          </option>
                        ))}
                      </Box>
                    </td>
                    <td>
                      <Box
                        component="input"
                        type="number"
                        min={1}
                        value={
                          Number.isFinite(rule.durationAfterStartDate)
                            ? rule.durationAfterStartDate
                            : ""
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateRule(index, {
                            durationAfterStartDate: e.target.valueAsNumber,
                          })
                        }
                        sx={{ ...flyoutInputSx, py: "6px" }}
                      />
                    </td>
                    <td>
                      <Box
                        component="select"
                        value={rule.unit}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          updateRule(index, {
                            unit: e.target.value as RetentionPeriodUnit,
                          })
                        }
                        sx={{ ...flyoutSelectSx, py: "6px" }}
                      >
                        {RETENTION_PERIOD_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {t(`transcriptionConfig:units.${unit}`)}
                          </option>
                        ))}
                      </Box>
                    </td>
                    <td>
                      <Box
                        component="button"
                        type="button"
                        aria-label={t("transcriptionConfig:deleteRule")}
                        onClick={() => removeRule(index)}
                        sx={{
                          ...iconBtnSx(false),
                          "&:hover": {
                            backgroundColor: "rgba(251,113,133,0.1)",
                            color: "var(--signal-rose)",
                          },
                        }}
                      >
                        ✕
                      </Box>
                    </td>
                  </Box>
                ))}
              </tbody>
            </Box>
          )}
          <Box
            component="button"
            type="button"
            onClick={addRule}
            sx={{ ...toolbarBtnSx(false), mt: 1.5 }}
          >
            + {t("transcriptionConfig:addRule")}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: "1px solid var(--border-subtle)",
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            sx={saveBtnSx}
          >
            {t("common:save")}
          </Box>
          <Box component="button" onClick={() => navigate(-1)} sx={cancelBtnSx}>
            {t("common:cancel")}
          </Box>
          {validationError && <Box sx={formErrorSx}>{validationError}</Box>}
          {saveMessage && (
            <Box
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 500,
                color:
                  saveMessage.type === "success"
                    ? "var(--accent)"
                    : "var(--signal-rose)",
              }}
            >
              {saveMessage.text}
            </Box>
          )}
        </Box>
    </SettingsPageShell>
  );
}
